import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CircleSlash } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export type MailContextMenuItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  danger?: boolean;
  disabled?: boolean;
  dividerAfter?: boolean;
  kind?: "default" | "color-header" | "color" | "color-clear";
  colorSwatch?: string;
  active?: boolean;
};

type Props = {
  open: boolean;
  x: number;
  y: number;
  items: MailContextMenuItem[];
  onAction: (actionId: string) => void;
  onClose: () => void;
};

const VIEWPORT_MARGIN = 8;

function clampMenuPosition(
  x: number,
  y: number,
  width: number,
  height: number
): { left: number; top: number } {
  const maxW = window.innerWidth - VIEWPORT_MARGIN * 2;
  const maxH = window.innerHeight - VIEWPORT_MARGIN * 2;

  let left = x;
  let top = y;

  if (width > maxW) {
    left = VIEWPORT_MARGIN;
  } else if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
    left = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  } else if (left < VIEWPORT_MARGIN) {
    left = VIEWPORT_MARGIN;
  }

  if (height > maxH) {
    top = VIEWPORT_MARGIN;
  } else if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
    const flipped = y - height;
    top =
      flipped >= VIEWPORT_MARGIN
        ? flipped
        : Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
  } else if (top < VIEWPORT_MARGIN) {
    top = VIEWPORT_MARGIN;
  }

  return { left, top };
}

export function MailContextMenu({ open, x, y, items, onAction, onClose }: Props) {
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return;

    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;

    const rect = menu.getBoundingClientRect();
    const { left, top } = clampMenuPosition(x, y, rect.width, rect.height);
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }, [open, x, y]);

  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onPointer(e: MouseEvent) {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    }

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  const colorItems = items.filter((item) => item.kind === "color");
  const colorClearItem = items.find((item) => item.kind === "color-clear");
  const regularItems = items.filter(
    (item) => item.kind !== "color" && item.kind !== "color-clear"
  );

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-[200] min-w-[12rem] max-w-[15rem] overflow-x-hidden overflow-y-auto rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] py-1 shadow-[var(--mail-shadow-lg)]"
      style={{
        left: x,
        top: y,
        maxHeight: `min(70vh, calc(100vh - ${VIEWPORT_MARGIN * 2}px))`,
      }}
    >
      {regularItems.map((item) => {
        if (item.kind === "color-header") {
          return (
            <div key={item.id}>
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
                {item.label}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2">
                {colorItems.map((colorItem) => (
                  <button
                    key={colorItem.id}
                    type="button"
                    role="menuitem"
                    title={colorItem.label}
                    aria-label={colorItem.label}
                    onClick={() => {
                      onAction(colorItem.id);
                      onClose();
                    }}
                    className={cn(
                      "h-5 w-5 rounded-full border-2 transition hover:scale-110",
                      colorItem.active
                        ? "border-[var(--mail-text)] ring-2 ring-[var(--mail-accent)]/30"
                        : "border-white/80"
                    )}
                    style={{ backgroundColor: colorItem.colorSwatch }}
                  />
                ))}
                {colorClearItem ? (
                  <button
                    key={colorClearItem.id}
                    type="button"
                    role="menuitem"
                    title={colorClearItem.label}
                    aria-label={colorClearItem.label}
                    onClick={() => {
                      onAction(colorClearItem.id);
                      onClose();
                    }}
                    className={cn(
                      "inline-flex h-5 w-5 items-center justify-center rounded-full border-2 transition hover:scale-110",
                      colorClearItem.active
                        ? "border-[var(--mail-text)] bg-[var(--mail-accent-soft)] ring-2 ring-[var(--mail-accent)]/30"
                        : "border-[var(--mail-border-soft)] bg-[var(--mail-surface-hover)]"
                    )}
                  >
                    <CircleSlash
                      className={cn(
                        "h-3 w-3",
                        colorClearItem.active ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]"
                      )}
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </button>
                ) : null}
              </div>
              {colorClearItem?.dividerAfter ? (
                <div className="border-t border-[var(--mail-border-soft)]" role="separator" />
              ) : null}
            </div>
          );
        }

        const Icon = item.icon;
        return (
          <div key={item.id}>
            <button
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                onAction(item.id);
                onClose();
              }}
              className={cn(
                "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition",
                item.danger
                  ? "text-[var(--mail-danger-text)] hover:bg-[var(--mail-danger-soft)]"
                  : "text-[var(--mail-text)] hover:bg-[var(--mail-surface-hover)]",
                item.disabled ? "cursor-not-allowed opacity-40" : ""
              )}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden /> : null}
              <span>{item.label}</span>
            </button>
            {item.dividerAfter ? (
              <div className="my-1 border-t border-[var(--mail-border-soft)]" role="separator" />
            ) : null}
          </div>
        );
      })}
    </div>,
    document.body
  );
}
